// v2 truck — a cab-over flatbed: a tall flat-faced slate-blue cab sitting right
// over the front axle (near-vertical windscreen, grille, round headlights, an
// exhaust stack at the cab's rear corner), behind it a low-boarded flatbed
// spanned by three pale canvas hoops, all riding a visible narrow chassis rail
// with a side-slung fuel tank and a front/rear bogie of six wheels. The cab faces
// +z. Ported 1:1 from age-of-mcp's v2/truck.ts (the source uses no shared
// helpers — only the local `axle` wheel-pair builder, inlined here).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const CAB    = "#4a6e8a";   // slate-blue cab
const ROOF   = "#3d5c74";   // darker cab roof
const GLASS  = "#aacdd9";   // windscreen
const GRILLE = "#2e2a26";   // front grille
const LIGHT  = "#f2e2a8";   // round headlight lenses
const CHASS  = "#332f2a";   // chassis rail & bumper
const BED    = "#7a5a36";   // flatbed planking
const BOARD  = "#5e4426";   // low side boards & headboard
const HOOP   = "#ddd3ba";   // pale canvas hoops
const WHEEL  = "#26211b";   // tyres
const HUB    = "#8a8580";   // hubs
const TANK   = "#6a655e";   // fuel tank
const PIPE   = "#4e4a44";   // exhaust stack

const HW = 0.17;    // half-width (x)
const WR = 0.075;   // wheel radius

// A wheel pair (tyre + hub each side) on an axle at z.
function axle(z: number): Part[] {
  const out: Part[] = [];
  for (const sx of [-1, 1]) {
    out.push({
      geometry: new THREE.CylinderGeometry(WR, WR, 0.05, 10),
      color: WHEEL, position: [sx * (HW + 0.01), WR, z],
      rotation: [0, 0, Math.PI / 2],
    });
    out.push({
      geometry: new THREE.CylinderGeometry(0.028, 0.028, 0.06, 6),
      color: HUB, position: [sx * (HW + 0.02), WR, z],
      rotation: [0, 0, Math.PI / 2],
    });
  }
  return out;
}

function truckParts(): Part[] {
  const parts: Part[] = [];

  // Visible chassis rail (narrower than the body, so it reads).
  parts.push({ geometry: new THREE.BoxGeometry(0.24, 0.045, 0.68), color: CHASS, position: [0, 0.0975, 0] });

  // Wheels: front axle under the cab + rear bogie under the bed.
  parts.push(...axle(0.22));
  parts.push(...axle(-0.14));
  parts.push(...axle(-0.26));

  // Cab-over (+z): tall flat-faced cab right over the front axle.
  parts.push({ geometry: new THREE.BoxGeometry(0.34, 0.26, 0.20), color: CAB, position: [0, 0.245, 0.26] });
  parts.push({ geometry: new THREE.BoxGeometry(0.345, 0.025, 0.205), color: ROOF, position: [0, 0.388, 0.26] });
  // Near-vertical windscreen on the front face, raked back a touch.
  parts.push({ geometry: new THREE.BoxGeometry(0.28, 0.115, 0.015), color: GLASS, position: [0, 0.30, 0.355], rotation: [0.08, 0, 0] });
  // Grille below the screen, with one round headlight each side.
  parts.push({ geometry: new THREE.BoxGeometry(0.26, 0.07, 0.015), color: GRILLE, position: [0, 0.16, 0.357] });
  for (const sx of [-1, 1]) {
    parts.push({
      geometry: new THREE.CylinderGeometry(0.024, 0.024, 0.022, 8),
      color: LIGHT, position: [sx * 0.115, 0.155, 0.365],
      rotation: [Math.PI / 2, 0, 0],
    });
  }
  // Front bumper + exhaust stack at the cab's rear corner.
  parts.push({ geometry: new THREE.BoxGeometry(0.36, 0.045, 0.035), color: CHASS, position: [0, 0.105, 0.372] });
  parts.push({ geometry: new THREE.CylinderGeometry(0.014, 0.014, 0.18, 5), color: PIPE, position: [0.15, 0.30, 0.145] });

  // Flatbed (-z): planked floor, low boards, headboard.
  parts.push({ geometry: new THREE.BoxGeometry(0.34, 0.035, 0.46), color: BED, position: [0, 0.1625, -0.105] });
  parts.push({ geometry: new THREE.BoxGeometry(0.34, 0.14, 0.02), color: BOARD, position: [0, 0.23, 0.115] });
  for (const sx of [-1, 1]) {
    parts.push({ geometry: new THREE.BoxGeometry(0.022, 0.05, 0.46), color: BOARD, position: [sx * 0.159, 0.205, -0.105] });
  }
  parts.push({ geometry: new THREE.BoxGeometry(0.30, 0.05, 0.02), color: BOARD, position: [0, 0.205, -0.325] });

  // Canvas hoops: three half-torus arches spanning the bed. A half torus
  // (arc = pi) is authored arching over local +y across x — exactly the hoop
  // shape — its feet hidden inside the side boards.
  for (const z of [-0.04, -0.16, -0.28]) {
    parts.push({
      geometry: new THREE.TorusGeometry(0.155, 0.011, 4, 6, Math.PI),
      color: HOOP, position: [0, 0.18, z],
    });
  }

  // Side-slung fuel tank on the chassis.
  parts.push({
    geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.16, 7),
    color: TANK, position: [0.15, 0.085, 0.04],
    rotation: [Math.PI / 2, 0, 0],
  });

  return parts;
}

export const truckV2 = defineModel({
  id: "truck-v2",
  batchId: "fable",
  name: "Truck (v2)",
  tags: ["vehicle","truck","low-poly","age-of-mcp","fable"],
  summary: "Low-poly truck.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.5,
  parts: truckParts,
  animations: [],
});
